// ─────────────────────────────────────────────────────────────────────────────
//  services/rpc.js — Thin Supabase RPC caller (raw REST, no SDK dependency)
//
//  All commands.* functions accept (p_idempotency_key text, p_payload jsonb).
//  This module provides:
//    rpc(fnName, idempotencyKey, payload, token) → { success, data } | throws AppError
//
//  Reads go through queries.* SECURITY DEFINER functions (see queries.js).
//  Direct table SELECT is NOT used — the RLS model (020) is function-only
//  for all business/financial tables.
//
//  Error mapping: Postgres RAISE EXCEPTION messages that match the frozen error
//  catalogue (ERR_PERMISSION, ERR_VALIDATION, etc.) are converted to AppError.
//  Unknown Postgres errors become ERR_INTERNAL.
// ─────────────────────────────────────────────────────────────────────────────
import { config } from '../platform/config';
import { AppError, ERRORS } from '../platform/errors';
import { ok } from '../platform/response';

const RPC_URL = (fn) => `${config.SUPABASE_URL}/rest/v1/rpc/${fn}`;

const baseHeaders = (token) => ({
  'Content-Type': 'application/json',
  apikey: config.SUPABASE_ANON_KEY,
  Authorization: `Bearer ${token}`,
});

// ── Session-expired event (reuse the same pattern as supabaseDB.js) ──────────
const fireSessionExpired = () => {
  try { window.dispatchEvent(new CustomEvent('faida:session-expired')); } catch {}
};

// ── Map Postgres error messages to catalogue codes ───────────────────────────
const PG_ERROR_MAP = Object.keys(ERRORS); // e.g. ['ERR_PERMISSION', 'ERR_VALIDATION', ...]

function parseRpcError(body) {
  // Supabase wraps Postgres RAISE EXCEPTION in { message, details, hint, code }
  const msg = body?.message || body?.error || '';
  const match = PG_ERROR_MAP.find((code) => msg.includes(code));
  if (match) return match;
  // Check for Postgres error codes we care about
  if (body?.code === '23505') return 'ERR_VALIDATION'; // unique violation
  if (body?.code === '23503') return 'ERR_VALIDATION'; // FK violation
  return 'ERR_INTERNAL';
}

// ── Generate idempotency keys ────────────────────────────────────────────────
let _seq = 0;
export function idempotencyKey(prefix = 'cmd') {
  _seq += 1;
  return `${prefix}-${Date.now()}-${_seq}-${Math.random().toString(36).slice(2, 8)}`;
}

// ── RPC call: POST /rest/v1/rpc/{fn} ─────────────────────────────────────────
// Commands are called via public-schema wrappers that delegate to commands.*
// SECURITY DEFINER functions (patch 005). No Accept-Profile header needed.
export async function rpc(fnName, idempotencyKey, payload, token) {
  if (!token) {
    fireSessionExpired();
    throw new AppError('ERR_PERMISSION', 'Not authenticated');
  }

  const headers = {
    ...baseHeaders(token),
    // NO Accept-Profile or Content-Profile — uses public schema wrappers
  };

  const body = {
    p_idempotency_key: idempotencyKey,
    p_payload: payload,
  };

  let res;
  try {
    res = await fetch(RPC_URL(fnName), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch (err) {
    // Network error
    throw new AppError('ERR_INTERNAL', `Network error: ${err.message}`);
  }

  if (res.status === 401) {
    fireSessionExpired();
    throw new AppError('ERR_PERMISSION', 'Session expired');
  }

  const responseBody = await res.json().catch(() => null);

  if (!res.ok) {
    const code = parseRpcError(responseBody);
    throw new AppError(code, responseBody?.message || ERRORS[code]?.message);
  }

  // commands.* return a JSONB result directly
  return ok(responseBody);
}

// NOTE: Direct table SELECT (GET /rest/v1/{table}) is intentionally NOT provided.
// The RLS model (020) is function-only for business/financial tables.
// All reads go through queries.* SECURITY DEFINER functions (see queries.js).
